import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { toast } from 'react-toastify';
import OfferDetailModal from './OfferDetailModal';
// Importez le tableau des réservations en présentiel que nous avons créé
import PresentielReservationsTable from './PresentielReservationsTable'; 

const PendingValidation = () => {
  const [items, setItems] = useState([]);
  const [financialPendingItems, setFinancialPendingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  // États pour le modal de détails
  const [selectedItem, setSelectedItem] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
  const token = localStorage.getItem('token');

  // Chargement global (tous les pending items)
  const fetchPendingItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/admin/offres/pending-items`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      setItems(response.data.data || []);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement pending items :', err);
      setError('Erreur lors du chargement des éléments en attente.');
    } finally {
      setLoading(false);
    }
  };

  // Chargement spécifique avec votre route financial-pending pour l'onglet dédié
  const fetchFinancialPendingItems = async () => {
    try {
      const response = await axios.get(`${apiUrl}/admin/offres/financial-pending`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const dataWithotType = (response.data.data || []).map(item => ({
        ...item,
        item_type: item.item_type || 'offre'
      }));
      setFinancialPendingItems(dataWithotType);
    } catch (err) {
      console.error('Erreur chargement financial-pending :', err);
    }
  };

  useEffect(() => {
    fetchPendingItems();
    fetchFinancialPendingItems();
  }, []);

  // Fonction unifiée pour modifier le statut global (valider, refuser, en_attente)
  const handleUpdateStatusOffer = async (id, statusAction) => {
    try {
      await axios.patch(
        `${apiUrl}/admin/offres/${id}/status`,
        { action: statusAction },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      const actionLabels = {
        valider: 'validée',
        refuser: 'refusée',
        en_attente: 'mise en attente',
      };

      toast.success(`Offre ${actionLabels[statusAction] || 'mise à jour'} avec succès.`);
      fetchPendingItems();
      fetchFinancialPendingItems(); 
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || "Erreur lors de la mise à jour de l'offre.";
      toast.error(errorMessage);
    }
  };

  // Fonction pour gérer la validation financière
  const handleUpdateFinancialValidation = async (id, financialStatus) => {
    try {
      await axios.put(
        `${apiUrl}/admin/offres/${id}/financial-validation`,
        { is_validated_financial: financialStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      toast.success(financialStatus ? 'Validation financière effectuée avec succès.' : 'Validation financière retirée.');
      fetchPendingItems();
      fetchFinancialPendingItems();
      
      if (selectedItem && (selectedItem.id_offre === id)) {
        setSelectedItem(prev => ({
          ...prev,
          info_offre: {
            ...prev.info_offre,
            is_validated_financial: financialStatus
          }
        }));
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err.response?.data?.message || "Erreur lors de la mise à jour financière.";
      toast.error(errorMessage);
    }
  };

  const handleOpenModal = (item) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setOpenModal(false);
  };

  // Détermination des éléments à afficher selon l'onglet actif
  const getDisplayedItems = () => {
    if (tabValue === 1) return items.filter(i => i.item_type === 'offre');
    if (tabValue === 2) return items.filter(i => i.item_type === 'workshop');
    if (tabValue === 3) return financialPendingItems;
    return items; 
  };

  const displayedItems = getDisplayedItems();

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#2B3674' }}>
            Validation des publications & Finances
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gérez et validez les offres, ateliers, finances et réservations en présentiel.
          </Typography>
        </Box>
      </Stack>

      <Paper elevation={0} sx={{ mb: 3, borderRadius: '16px', border: '1px solid #eceef0', bgcolor: '#F4F7FE', p: 1 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          textColor="primary"
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { fontWeight: 700, textTransform: 'none', fontSize: '14px', borderRadius: '12px' } }}
        >
          <Tab label={`Tous (${items.length})`} />
          <Tab label={`Soins individuels (${items.filter(i => i.item_type === 'offre').length})`} />
          <Tab label={`Ateliers (${items.filter(i => i.item_type === 'workshop').length})`} />
          <Tab label={`Finances en attente (${financialPendingItems.length})`} />
          <Tab label="📍 Réservations Présentiel (Locaux SOA)" />
        </Tabs>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* 🚀 Condition d'affichage : Si tabValue === 4, on affiche le tableau des réservations en présentiel, sinon le tableau classique des offres */}
      {tabValue === 4 ? (
        <PresentielReservationsTable />
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '20px', border: '1px solid #eceef0', boxShadow: '0px 4px 12px rgba(0,0,0,0.03)' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F4F7FE' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#2B3674' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2B3674' }}>Titre & Détails</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2B3674' }}>Professionnel</TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#2B3674' }}>Catégorie / Prix</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#2B3674' }}>Statut & Finance</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: '#2B3674' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : displayedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    Aucun élément en attente de validation dans cette catégorie.
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.main?.map((item) => {
                  // Rendu de votre tableau d'offres existant...
                }) || displayedItems.map((item) => {
                  const isOffer = item.item_type === 'offre';
                  const title = isOffer ? item.info_offre?.titre_offre : item.title_ws;
                  const price = isOffer ? item.info_offre?.prix_offre : null;
                  const categoryTitle = item.category?.title_category || 'N/A';
                  const itemId = isOffer ? item.id_offre : item.id_ws;
                  const intervenantName = item.intervenant 
                    ? `${item.intervenant.prenom_user || ''} ${item.intervenant.nom_user || ''}`
                    : 'Inconnu';
                  
                  const isFinancialValid = item.info_offre?.is_validated_financial === 1;

                  return (
                    <TableRow key={`${item.item_type}-${itemId}`} hover>
                      <TableCell>
                        <Chip 
                          label={isOffer ? 'Offre de soin' : 'Atelier'} 
                          color={isOffer ? 'primary' : 'secondary'} 
                          size="small" 
                          sx={{ fontWeight: 600, borderRadius: '8px' }} 
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, color: '#2B3674', fontSize: '14px' }}>{title || 'Sans titre'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Créé le : {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{intervenantName}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.intervenant?.email_user}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{categoryTitle}</Typography>
                        {price && <Typography variant="caption" color="success.main" sx={{ fontWeight: 700 }}>{price} €</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="column" spacing={0.5} alignItems="center">
                          <Chip label="En attente" color="warning" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                          {isOffer && (
                            <Chip
                              label={isFinancialValid ? 'Finance validée' : 'Finance non validée'}
                              color={isFinancialValid ? 'success' : 'default'}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '10px' }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenModal(item)}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                          >
                            Détails
                          </Button>
                          
                          {isOffer && tabValue === 3 ? (
                            <Button
                              variant={isFinancialValid ? "outlined" : "contained"}
                              color="info"
                              size="small"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleUpdateFinancialValidation(itemId, isFinancialValid ? 0 : 1)}
                              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                            >
                              {isFinancialValid ? 'Annuler finance' : 'Valider finance'}
                            </Button>
                          ) : (
                            isOffer && (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  onClick={() => handleUpdateStatusOffer(itemId, 'valider')}
                                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                                >
                                  Valider
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={() => handleUpdateStatusOffer(itemId, 'refuser')}
                                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                                >
                                  Refuser
                                </Button>
                                <Button
                                  variant={isFinancialValid ? "outlined" : "contained"}
                                  color="info"
                                  size="small"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => handleUpdateFinancialValidation(itemId, isFinancialValid ? 0 : 1)}
                                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
                                >
                                  {isFinancialValid ? 'Annuler finance' : 'Valider finance'}
                                </Button>
                              </>
                            )
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <OfferDetailModal
        open={openModal}
        onClose={handleCloseModal}
        item={selectedItem}
        onUpdateStatus={handleUpdateStatusOffer}
        onUpdateFinancialValidation={handleUpdateFinancialValidation}
      />
    </Box>
  );
};

export default PendingValidation;